import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CaratConversionCalculator = () => {
  const [inputData, setInputData] = useState({
    length: '',
    width: '',
    height: '',
    result: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCarat = () => {
    const { length, width, height } = inputData;
    
    // Validate input
    if (!length || !width || !height) {
      setError('Please enter all dimensions');
      return;
    }

    // Validate numeric input
    if (isNaN(length) || isNaN(width) || isNaN(height)) {
      setError('Please enter valid numbers for all dimensions');
      return;
    }

    // Convert to numbers
    const numericLength = parseFloat(length);
    const numericWidth = parseFloat(width);
    const numericHeight = parseFloat(height);

    // Calculate carat weight (approximate formula)
    const carat = (numericLength * numericWidth * numericHeight) / 4;
    const roundedCarat = carat.toFixed(2);

    // Create visualization data
    const dimensions = [numericLength, numericWidth, numericHeight, carat];
    const labels = ['Length (mm)', 'Width (mm)', 'Height (mm)', 'Estimated Carat'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Values',
        data: dimensions,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Multiply the length, width, and height',
      `Product: ${numericLength} × ${numericWidth} × ${numericHeight} = ${numericLength * numericWidth * numericHeight}`,
      '\nStep 2: Divide the product by 4 to estimate carat weight',
      `Estimated Carat: (${numericLength} × ${numericWidth} × ${numericHeight}) / 4 = ${roundedCarat}`
    ];

    setResults({
      length: numericLength,
      width: numericWidth,
      height: numericHeight,
      carat: roundedCarat,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    length: '6',
    width: '5',
    height: '3.5',
    carat: '2.625'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Carat Conversion Calculator | MM to Carat Weight Tool</title>
          <meta name="description" content="Convert millimeters to estimated carat weight for gemstones. This calculator helps estimate the carat weight based on gemstone dimensions." />
          <meta name="keywords" content="carat calculator, mm to carat, gemstone calculator, jewelry tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">💎</div>
            <h1 className="text-3xl font-bold text-gray-800">Carat Conversion Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Estimate the carat weight of a gemstone based on its dimensions in millimeters. This calculator uses an approximate formula to convert measurements to carat weight.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Length (mm):</label>
              <input
                type="text"
                name="length"
                value={inputData.length}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter length in millimeters"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Width (mm):</label>
              <input
                type="text"
                name="width"
                value={inputData.width}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter width in millimeters"
              />
            </div>
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Height (mm):</label>
              <input
                type="text"
                name="height"
                value={inputData.height}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter height in millimeters"
              />
            </div>
          </div>

          <button
            onClick={calculateCarat}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Carat
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Carat Calculation</h3>
                  <p className="text-lg">Length: {results.length} mm</p>
                  <p className="text-lg">Width: {results.width} mm</p>
                  <p className="text-lg">Height: {results.height} mm</p>
                  <p className="text-lg font-bold mt-4">Estimated Carat: {results.carat} ct</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Volume: {(results.length * results.width * results.height).toFixed(2)} mm³</p>
                  <p className="text-lg">Formula: (L × W × H) / 4</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Values'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Dimensions'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Gemstone Dimensions and Estimated Carat'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for carat conversion:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Length: {exampleData.length} mm</li>
                <li>Width: {exampleData.width} mm</li>
                <li>Height: {exampleData.height} mm</li>
                <li>Estimated Carat: {exampleData.carat} ct</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Multiply the dimensions
                  <ul className="list-disc pl-6">
                    <li>6 × 5 × 3.5 = 105 mm³</li>
                  </ul>
                </li>
                <li>Divide by 4 to get carat weight
                  <ul className="list-disc pl-6">
                    <li>105 / 4 = 26.25 points</li>
                    <li>26.25 points = 2.625 carats</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Carat Conversion Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Dimensions:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the length, width, and height in millimeters</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The volume of the gemstone</li>
                  <li>The estimated carat weight</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the estimated carat weight</li>
                  <li>Check the volume calculation</li>
                  <li>View the visual representation of dimensions</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>1 carat = 100 points</li>
                <li>This is an approximate calculation</li>
                <li>Actual carat weight may vary based on gemstone density</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Carat Weight <span className="text-blue-600">💎</span></h2>
          <div className="text-gray-600">
            <p>
              Carat weight is one of the "4Cs" used to evaluate gemstones. It measures the mass of a gemstone where 1 carat equals 200 milligrams. The relationship between size and carat weight isn't linear because gemstones have different densities and shapes.
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Carat vs. Karat:</strong> Carat measures weight, while karat measures purity</li>
              <li><strong>Point System:</strong> 1 point = 0.01 carats</li>
              <li><strong>Approximation:</strong> This calculator provides an estimate based on dimensions</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Measure dimensions carefully</li>
              <li><strong>Consult Professionals:</strong> For valuable gemstones, get professional appraisal</li>
              <li><strong>Multiple Measurements:</strong> Take multiple measurements for irregular shapes</li>
              <li><strong>Consider Density:</strong> Different gemstones have different densities</li>
            </ul>
            <p className="italic mt-4">
              Note: This calculator provides an estimate only. Actual carat weight may vary.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CaratConversionCalculator;